'use client'

import { useEffect, useState } from 'react'
import { cn } from '@/utilities/ui'

type Heading = { id: string; text: string }

export function AktuellTOC({ headings }: { headings: Heading[] }) {
  const [activeId, setActiveId] = useState<string>(headings[0]?.id || '')

  useEffect(() => {
    if (headings.length === 0) return

    const handleScroll = () => {
      let current = headings[0]?.id || ''
      for (const h of headings) {
        const el = document.getElementById(h.id)
        if (!el) continue
        if (el.getBoundingClientRect().top < 300) {
          current = h.id
        }
      }
      setActiveId(current)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [headings])

  const onClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault()
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 100
    window.scrollTo({ top: y, behavior: 'smooth' })
    window.history.pushState(null, '', `#${id}`)
  }

  if (headings.length === 0) return null

  return (
    <nav aria-label="Inhaltsverzeichnis" className="flex flex-col">
      <p className="label-md text-text-secondary mb-8 tracking-widest">Inhalt</p>
      <div className="relative flex flex-col">
        <div className="absolute left-0 top-0 bottom-0 w-px bg-border-brand" />
        {headings.map((h, idx) => (
          <a
            key={h.id}
            href={`#${h.id}`}
            onClick={(e) => onClick(e, h.id)}
            className="group relative flex gap-4 py-4 pl-6 z-10"
          >
            <span
              className={cn(
                'absolute left-0 top-0 bottom-0 w-px transition-colors',
                activeId === h.id ? 'bg-brand' : 'bg-transparent group-hover:bg-border-brand',
              )}
            />
            <span
              className={cn(
                'label-md text-sm shrink-0 mt-1 transition-colors',
                activeId === h.id ? 'text-text-secondary' : 'text-text-secondary/60',
              )}
            >
              {(idx + 1).toString().padStart(2, '0')}
            </span>
            <span
              className={cn(
                'subtitle-md leading-snug transition-colors',
                activeId === h.id
                  ? 'text-text-primary font-medium'
                  : 'text-text-secondary group-hover:text-text-primary',
              )}
            >
              {h.text}
            </span>
          </a>
        ))}
      </div>
    </nav>
  )
}
